import { motion } from "framer-motion";
import { useContext } from "react";
import { Link } from "react-router-dom";
import { DataContext } from "../../Context/DataContext";
import { Helmet } from "react-helmet-async"; 
import { MonitorPlay, Trophy, Zap, ArrowRight, ShieldCheck } from "lucide-react";

const GameGallery = () => {
  const gameData = useContext(DataContext);

  const containerVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut",
        staggerChildren: 0.15, 
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, scale: 0.9, y: 30 },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

 const createSlug = (text) => {
    return text
      ?.toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": gameData?.slice(11, 17)?.map((game, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": game?.name,
      "url": `https://www.gamezonehub.in/play/${createSlug(game?.name)}`
    }))
  };

  return (
    <motion.section className="bg-gradient-to-b lg:px-8 from-[#0f172a] to-[#1e293b] sm:py-20 py-12">
       <Helmet>
        {schemaData && (
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />
        )}
      </Helmet>
      <div className="container mx-auto px-4 ">
        
        {/* 🚀 EXPANDED HEADER TEXT FOR SEO */}
        <motion.h2 className="text-center text-2xl sm:text-3xl md:text-4xl xl:text-5xl font-extrabold text-white mb-6" initial={{ y: 30, opacity: 0 }} whileInView={{ y: 0, opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.8, ease: "easeOut" }}>
          🎮 Explore Our{" "}
          <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-300">
            Free Online Games
          </span>
        </motion.h2>

        <motion.div 
          className="text-center text-gray-400 sm:text-lg text-[16px] mb-16 max-w-5xl mx-auto leading-relaxed space-y-4" 
          initial={{ y: 20, opacity: 0 }} 
          animate={{ y: 0, opacity: 1 }} 
          transition={{ duration: 0.9, ease: "easeOut", delay: 0.2 }}
        >
          <p>
            Dive into our extensive, hand-picked gallery featuring the absolute best <strong>free online games</strong> available on the internet. We continuously update this selection to ensure you have access to the highest-rated and most mechanically engaging titles across every genre.
          </p>
          <p>
             Whether you enjoy the adrenaline rush of high-speed 3D racing, the mental workout of highly strategic puzzles, or the endless thrill of classic arcade action, Game Zone Hub has a title perfectly suited for you. Experience premium, lag-free HTML5 gaming directly in your web browser with absolutely <strong>no installation, no malware risks, and no downloads required</strong>. Just click, play, and conquer!
          </p>
        </motion.div>

        {/* GAMES GRID */}
        <motion.ul className="grid sm:grid-cols-2 md:grid-cols-3 gap-8" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }}>
          {gameData?.slice(11, 17)?.map((game, index) => {
             const gameSlug = createSlug(game?.name);

             return (
              <motion.li 
                key={index} 
                variants={cardVariants} 
                className="relative group rounded-3xl overflow-hidden shadow-lg cursor-pointer border border-white/5 hover:border-cyan-400/30 list-none bg-[#111827]"
              >
                <Link to={`/play/${gameSlug}`} state={{ game: game }} aria-label={`Play ${game?.name} online`}>
                  
                  <motion.div 
                    whileHover={{ scale: 1.05, transition: { duration: 0.4, ease: "easeInOut" } }}
                    className="h-full w-full relative"
                  >
                    <motion.img 
                      src={game.image} 
                      alt={`Play ${game?.name} - Free online browser game at Game Zone Hub`}
                      title={`Play ${game?.name}`}
                      className="w-full h-[250px] object-cover transition duration-500 group-hover:opacity-60" 
                      loading="lazy"
                      decoding="async"
                    />
                    
                    <div className="absolute inset-0 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition duration-300 bg-gradient-to-t from-black/90 via-black/40 to-transparent">
                      <motion.h3 
                        className="sm:text-2xl text-xl font-extrabold text-white mb-4 px-4 text-center drop-shadow-lg" 
                        initial={{ y: 15, opacity: 0 }} 
                        whileInView={{ y: 0, opacity: 1 }} 
                        transition={{ duration: 0.4, delay: 0.1 }}
                      >
                        {game?.name}
                      </motion.h3>
                      
                      <motion.div 
                        className="px-6 py-3 text-sm font-bold bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-full transition-all shadow-[0_0_20px_rgba(59,130,246,0.6)] flex items-center gap-2" 
                        whileTap={{ scale: 0.95 }} 
                        whileHover={{ scale: 1.05 }}
                      >
                        <MonitorPlay size={18}/> Play Now Free
                      </motion.div>
                    </div>
                  </motion.div>
                </Link>
              </motion.li>
            );
          })}
        </motion.ul>

        <motion.div 
          className="mt-14 flex justify-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <Link 
            to="/games" 
            className="flex items-center gap-3 px-10 py-4 bg-white/5 hover:bg-white/10 border border-white/20 hover:border-cyan-400/50 rounded-full text-white font-bold text-lg transition-all hover:scale-105 shadow-xl"
            aria-label="Explore all free online games"
          >
            Explore Full Game Library <ArrowRight size={20} className="text-cyan-400" />
          </Link>
        </motion.div>

        {/* 🚀 EXPANDED VALUE-ADDED CONTENT SECTION FOR ADSENSE */}
        <div className="sm:mt-24 mt-16 border-t border-white/10 pt-16" data-aos="fade-up">
          <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-6">
                Discover Your Next Favorite Game
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-purple-400 mx-auto rounded-full mb-6"></div>
              <p className="text-gray-400 max-w-3xl mx-auto leading-relaxed">
                  We are committed to providing a top-tier digital entertainment platform. Here is why millions of players choose our massive library of <strong>HTML5 browser games</strong> over traditional downloaded applications.
              </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 text-center md:text-left">

            <div
              data-aos="fade-up"
              data-aos-delay="200"
              className="group cursor-pointer bg-[#162032] p-8 rounded-3xl border border-white/5 
              transition-all duration-500 ease-out
              hover:-translate-y-2 hover:scale-[1.02]
              hover:border-blue-400/40
              hover:shadow-[0_15px_40px_-10px_rgba(59,130,246,0.5)] flex flex-col h-full"
            >
              <div className="bg-blue-500/10 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 mx-auto md:mx-0 group-hover:bg-blue-500/20 transition-colors">
                  <MonitorPlay className="text-blue-400 w-8 h-8 transition-transform duration-500 group-hover:scale-110" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Browser Optimized Engine</h3>
              <p className="text-gray-400 text-sm leading-relaxed flex-grow">
                Our HTML5 games are engineered for raw, uncompromising performance. We utilize advanced WebGL APIs to ensure you enjoy buttery-smooth 60 FPS graphics and instant load times. You can reliably <strong>play online games</strong> on Google Chrome, Apple Safari, Firefox, or Edge without ever experiencing frustrating lag, crashes, or the need to install third-party plugins.
              </p>
            </div>

            <div
              data-aos="fade-up"
              data-aos-delay="350"
              className="group cursor-pointer bg-[#162032] p-8 rounded-3xl border border-white/5 
              transition-all duration-500 ease-out
              hover:-translate-y-2 hover:scale-[1.02]
              hover:border-yellow-400/40
              hover:shadow-[0_15px_40px_-10px_rgba(234,179,8,0.5)] flex flex-col h-full"
            >
              <div className="bg-yellow-500/10 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 mx-auto md:mx-0 group-hover:bg-yellow-500/20 transition-colors">
                  <Trophy className="text-yellow-400 w-8 h-8 transition-transform duration-500 group-hover:rotate-12" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Competitive Daily Challenges</h3>
              <p className="text-gray-400 text-sm leading-relaxed flex-grow">
                Never get bored! Compete in exciting new levels and global tournaments every single day. Test your raw skills in intense strategy board games, complex logic puzzles, and high-speed action brawlers. Top the global leaderboards and prove you are the best player in our premium <strong>free browser games</strong> collection.
              </p>
            </div>

            <div
              data-aos="fade-up"
              data-aos-delay="500"
              className="group cursor-pointer bg-[#162032] p-8 rounded-3xl border border-white/5 
              transition-all duration-500 ease-out
              hover:-translate-y-2 hover:scale-[1.02]
              hover:border-purple-400/40
              hover:shadow-[0_15px_40px_-10px_rgba(168,85,247,0.5)] flex flex-col h-full"
            >
              <div className="bg-purple-500/10 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 mx-auto md:mx-0 group-hover:bg-purple-500/20 transition-colors">
                  <Zap className="text-purple-400 w-8 h-8 transition-transform duration-500 group-hover:scale-125" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Instant & Safe Access</h3>
              <p className="text-gray-400 text-sm leading-relaxed flex-grow">
                 We respect your privacy and value your time. That is why no accounts, email registrations, or credit cards are needed for the vast majority of our titles. Just click and <strong>play free online</strong> instantly in a secure, sandboxed environment. We prioritize a fast, seamless, and completely malware-free gaming experience for users of all ages.
              </p>
            </div>

          </div>
        </div>

      </div>
    </motion.section>
  );
};

export default GameGallery;